import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, CalendarOff } from 'lucide-react';
import {
  EVENT_TYPES,
  formatDateRange,
  formatDayHeading,
  formatDayShort,
  formatWeekdayShort,
  getIssuePhase,
  parseIso,
} from '../../utils/ipoCalendar';
import { BOARD_STYLES, TYPE_STYLES, dominantType } from './calendarStyles';

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const formatCr = (value) => `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
const formatInr = (value) => `₹${value.toLocaleString('en-IN')}`;
const priceBand = (issue) => {
  if (!issue.priceHigh) return null;
  return issue.priceLow && issue.priceLow !== issue.priceHigh ? `₹${issue.priceLow} – ₹${issue.priceHigh}` : `₹${issue.priceHigh}`;
};

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const DateTile = ({ iso, type }) => {
  const date = parseIso(iso);
  return (
    <div className={`w-12 flex-shrink-0 rounded-xl border text-center py-1.5 ${TYPE_STYLES[type]?.tile || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      <div className="text-[10px] font-extrabold tracking-wider">{MONTH_ABBR[date.getMonth()]}</div>
      <div className="text-lg font-extrabold leading-none mt-0.5">{String(date.getDate()).padStart(2, '0')}</div>
    </div>
  );
};

// Live feed rows can be missing size, price, lot and registrar -- skip those
// parts rather than rendering "null" or crashing on them.
const detailParts = (event) => {
  const { issue } = event;
  const size = issue.sizeCr ? `Size ${formatCr(issue.sizeCr)}` : null;
  const registrar = issue.registrar ? `Registrar: ${issue.registrar}` : null;
  if (event.type === 'open') {
    const band = priceBand(issue);
    return [
      size,
      band ? `Price ${band}` : null,
      issue.lotSize ? `Lot ${issue.lotSize.toLocaleString('en-IN')} shares` : null,
      issue.lotSize && issue.priceHigh ? `Min ${formatInr(issue.lotSize * issue.priceHigh)}` : null,
      `Window ${formatDateRange(issue.openOn, issue.closeOn)}`,
    ].filter(Boolean);
  }
  if (event.type === 'close') {
    return [
      size,
      typeof issue.subscription === 'number' ? `Subscribed ${issue.subscription.toFixed(2)}x` : null,
      registrar,
      'Cut-off 5:00 PM IST',
    ].filter(Boolean);
  }
  return [
    registrar,
    'Check with PAN or application number',
    issue.allotmentEstimated ? 'Expected T+1 after closing' : null,
  ].filter(Boolean);
};

// Primary call to action for an event, based on where the issue stands today
const primaryAction = (event, today) => {
  const { issue } = event;
  const phase = getIssuePhase(issue, today);
  if (event.type === 'open') {
    if (phase === 'open') {
      return { label: event.date === today ? 'Apply Opening Day' : 'Apply', tone: 'green', kind: 'apply' };
    }
    return phase === 'upcoming' ? null : { label: 'Bidding closed', tone: 'muted' };
  }
  if (event.type === 'close') {
    if (phase === 'open') {
      return event.date === today
        ? { label: 'Bid Final Hour', tone: 'red', kind: 'apply' }
        : { label: 'Apply Now', tone: 'orange', kind: 'apply' };
    }
    return phase === 'upcoming' ? null : { label: 'Closed at 5:00 PM', tone: 'muted' };
  }
  if (event.date <= today) return { label: 'Check Allotment', tone: 'teal', kind: 'allotment' };
  return { label: `Expected ${formatDayShort(event.date)}`, tone: 'muted' };
};

const ACTION_TONES = {
  green: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  orange: 'bg-orange-500 hover:bg-orange-600 text-white',
  red: 'bg-rose-600 hover:bg-rose-700 text-white',
  teal: 'bg-[#008080] hover:bg-[#006666] text-white',
};

const EventRow = ({ event, today, onSync, onAction }) => {
  if (event.type === 'holiday') {
    const { holiday } = event;
    return (
      <li className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-5 py-4 bg-slate-50/70">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <CalendarOff className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-slate-800">Trading Holiday — {holiday.name}</span>
              <span className={`px-1.5 py-px rounded border text-[10px] font-bold uppercase ${TYPE_STYLES.holiday.badge}`}>
                Market Closed
              </span>
            </div>
            {holiday.note && <p className="mt-1 text-xs text-slate-500">{holiday.note}</p>}
          </div>
        </div>
        <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">
          Bidding resumes {formatWeekdayShort(holiday.resumesOn)}
        </span>
      </li>
    );
  }

  const { issue } = event;
  const isToday = event.date === today;
  const typeLabel =
    event.type === 'allotment' && issue.allotmentEstimated && event.date > today
      ? 'Allotment (expected)'
      : `${EVENT_TYPES[event.type].label}${isToday ? ' Today' : ''}`;
  const action = primaryAction(event, today);
  const canSync = event.date >= today;

  return (
    <li className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-slate-50/60 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {issue.gmpSlug ? (
            <Link to={`/ipo-gmp/${issue.gmpSlug}`} className="text-sm font-bold text-slate-900 hover:text-[#008080]">
              {issue.company} IPO
            </Link>
          ) : (
            <span className="text-sm font-bold text-slate-900">{issue.company} IPO</span>
          )}
          <span className={`px-1.5 py-px rounded border text-[10px] font-bold uppercase ${TYPE_STYLES[event.type].badge}`}>
            {typeLabel}
          </span>
          <span className={`px-1.5 py-px rounded border text-[10px] font-bold uppercase ${BOARD_STYLES[issue.board].badge}`}>
            {BOARD_STYLES[issue.board].label}
          </span>
          {event.type === 'open' && typeof issue.gmp === 'number' && (
            <span className="text-[11px] font-bold text-emerald-600">
              GMP {issue.gmp >= 0 ? '+' : '−'}₹{Math.abs(issue.gmp)}
              {issue.priceHigh ? ` (${((issue.gmp / issue.priceHigh) * 100).toFixed(1)}%)` : ''}
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] sm:text-xs text-slate-500">{detailParts(event).join(' • ')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
        {canSync && (
          <button
            type="button"
            onClick={() => onSync(event)}
            aria-label={`Add ${issue.company} ${EVENT_TYPES[event.type].label.toLowerCase()} to calendar`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-[#008080] hover:text-[#008080] transition-colors"
          >
            <CalendarPlus className="w-3.5 h-3.5" />
            Sync
          </button>
        )}
        {event.type === 'open' && issue.hasProspectus && (
          <button
            type="button"
            onClick={() => onAction({ kind: 'prospectus', event })}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-slate-300 transition-colors"
          >
            RHP Prospectus
          </button>
        )}
        {issue.gmpSlug && event.type !== 'allotment' && (
          <Link
            to={`/ipo-gmp/${issue.gmpSlug}`}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-slate-300 transition-colors"
          >
            Details
          </Link>
        )}
        {action &&
          (action.tone === 'muted' ? (
            <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap">{action.label}</span>
          ) : (
            <button
              type="button"
              onClick={() => onAction({ kind: action.kind, event })}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-colors ${ACTION_TONES[action.tone]}`}
            >
              {action.label}
            </button>
          ))}
      </div>
    </li>
  );
};

const DayCountBadges = ({ events }) => {
  const counts = events.reduce((acc, event) => ({ ...acc, [event.type]: (acc[event.type] || 0) + 1 }), {});
  return ['open', 'close', 'allotment']
    .filter((type) => counts[type])
    .map((type) => (
      <span key={type} className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${TYPE_STYLES[type].badge}`}>
        {plural(counts[type], EVENT_TYPES[type].singular)}
      </span>
    ));
};

export const TimelineView = ({ days, today, dayNotes, highlights, selectedDay, onSync, onAction }) => (
  <ol className="space-y-4">
    {days.map(({ date, events }) => {
      const isToday = date === today;
      const holidayOnly = events.every((event) => event.type === 'holiday');
      const isBusiest = highlights.busiest.includes(date);
      const isMega = highlights.mega.includes(date);
      const accent = isBusiest
        ? 'border-amber-300 ring-1 ring-amber-200'
        : isMega
          ? 'border-emerald-300 ring-1 ring-emerald-200'
          : isToday
            ? 'border-teal-300 ring-1 ring-teal-200'
            : 'border-slate-200/90';

      return (
        <li
          key={date}
          id={`day-${date}`}
          className={`scroll-mt-24 rounded-2xl border bg-white shadow-sm overflow-hidden transition-shadow ${accent} ${
            selectedDay === date ? 'shadow-card' : ''
          }`}
        >
          <div
            className={`flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 border-b border-slate-100 ${
              isBusiest ? 'bg-amber-50/60' : isMega ? 'bg-emerald-50/60' : holidayOnly ? 'bg-slate-50' : ''
            }`}
          >
            <DateTile iso={date} type={dominantType(events)} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm sm:text-base font-extrabold text-slate-900">{formatDayHeading(date)}</h3>
                {isToday && (
                  <span className="px-2 py-0.5 rounded-full bg-[#008080] text-white text-[10px] font-extrabold uppercase tracking-wide">
                    Today
                  </span>
                )}
                {isBusiest && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-extrabold uppercase tracking-wide">
                    Busiest Day
                  </span>
                )}
                {isMega && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold uppercase tracking-wide">
                    Mega Launch Day
                  </span>
                )}
              </div>
              {dayNotes[date] && <p className="text-xs text-slate-500 mt-0.5">{dayNotes[date]}</p>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <DayCountBadges events={events} />
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {events.map((event) => (
              <EventRow key={event.id} event={event} today={today} onSync={onSync} onAction={onAction} />
            ))}
          </ul>
        </li>
      );
    })}
  </ol>
);

export default TimelineView;
